import { useState, useEffect, useCallback } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { Download, TrendingUp, Clock, CheckCircle, AlertCircle, Loader2, RefreshCw, BarChart3, PieChartIcon, Activity, Bot, Zap, ShieldAlert, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTenant } from '@/hooks/useTenant'
import { getAuthToken } from '@/hooks/useAuth'
import { API_BASE, getAgentMetrics, AgentMetricsData } from '@/services/api'

interface AnalyticsData {
  period: string
  total_conversations: number
  total_messages: number
  avg_response_time_ms: number
  resolution_rate: number
  escalation_rate: number
  categories: Record<string, number>
  daily_trend: Array<{ date: string; count: number }>
  hourly_distribution: number[]
}

const CATEGORY_COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#6B7280', '#EF4444']

// Module-level cache to persist data across navigation
let _cachedAnalytics: AnalyticsData | null = null
let _cachedDateRange: string = 'week'

export default function AnalyticsPage() {
  const { tenantId, isLoading: tenantLoading } = useTenant()
  const [dateRange, setDateRange] = useState(_cachedDateRange)
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(_cachedAnalytics)
  const [isLoading, setIsLoading] = useState(!_cachedAnalytics)
  const [error, setError] = useState<string | null>(null)
  const [agentMetrics, setAgentMetrics] = useState<AgentMetricsData | null>(null)

  const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token && token !== 'demo-token') {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }, [tenantId])

  useEffect(() => {
    if (tenantId && !tenantLoading) {
      loadAnalytics()
    }
  }, [dateRange, tenantId, tenantLoading])

  const loadAnalytics = async () => {
    if (!tenantId) return

    if (!_cachedAnalytics) {
      setIsLoading(true)
    }
    setError(null)

    try {
      const headers = await getHeaders()
      const response = await fetch(`${API_BASE}/v1/analytics?period=${dateRange}`, { headers })

      if (!response.ok) {
        throw new Error('Failed to load analytics')
      }

      const data = await response.json()
      setAnalytics(data)
      _cachedAnalytics = data
      _cachedDateRange = dateRange

      // Also load agent metrics for AI Automation section
      try {
        const metrics = await getAgentMetrics()
        setAgentMetrics(metrics)
      } catch {
        // Agent metrics are optional, don't fail the page
      }
    } catch (err) {
      console.error('Error loading analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setIsLoading(false)
    }
  }

  // Transform data for charts — convert raw counts to percentages
  const categoryData = analytics?.categories
    ? (() => {
        const entries = Object.entries(analytics.categories);
        const total = entries.reduce((sum, [, val]) => sum + (val as number), 0);
        return total > 0
          ? entries.map(([name, value], index) => ({
              name,
              value: Math.round(((value as number) / total) * 100),
              count: value as number,
              color: CATEGORY_COLORS[index % CATEGORY_COLORS.length]
            }))
          : [];
      })()
    : []

  const conversationData = analytics?.daily_trend?.map(item => ({
    name: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
    conversations: item.count,
    resolved: Math.round(item.count * (analytics?.resolution_rate || 0) / 100)
  })) || []

  const responseTimeData = analytics?.hourly_distribution?.map((count, hour) => ({
    hour: `${hour.toString().padStart(2, '0')}:00`,
    conversations: count
  })) || []

  // Calculate metrics
  const totalConversations = analytics?.total_conversations || 0
  const resolutionRate = analytics?.resolution_rate || 0
  const avgResponseTime = analytics?.avg_response_time_ms ? (analytics.avg_response_time_ms / 1000).toFixed(1) : '0.0'
  const escalatedCases = Math.round((analytics?.escalation_rate || 0) * totalConversations / 100)

  if (isLoading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 mt-1">Insights and performance metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="day">Last 24 hours</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
          </select>
          <button
            onClick={loadAnalytics}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-blue-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            {totalConversations > 0 && (
              <span className="text-sm text-green-600 font-medium">Active</span>
            )}
          </div>
          <p className="text-3xl font-bold mt-4">{totalConversations.toLocaleString()}</p>
          <p className="text-gray-500 text-sm">Total Conversations</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            {resolutionRate > 90 && (
              <span className="text-sm text-green-600 font-medium">Excellent</span>
            )}
          </div>
          <p className="text-3xl font-bold mt-4">{resolutionRate.toFixed(1)}%</p>
          <p className="text-gray-500 text-sm">Resolution Rate</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4">{avgResponseTime}s</p>
          <p className="text-gray-500 text-sm">Avg Response Time</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4">{escalatedCases}</p>
          <p className="text-gray-500 text-sm">Escalated Cases</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversations Over Time */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-4">Conversations Overview</h3>
          {conversationData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={conversationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="conversations" fill="#FCD34D" name="Total" />
                <Bar dataKey="resolved" fill="#10B981" name="Resolved" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex flex-col items-center justify-center text-center px-4">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-3">
                <BarChart3 className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-sm font-medium text-gray-600">No conversation data yet</p>
              <p className="text-xs text-gray-400 mt-1 max-w-[240px]">
                Data will appear here once your AI pipeline processes incoming emails.
              </p>
              <Link to="/ai-settings" className="text-xs font-medium text-amber-600 hover:text-amber-700 mt-2">
                Configure AI Settings
              </Link>
            </div>
          )}
        </div>

        {/* Category Distribution */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-4">Category Distribution</h3>
          {categoryData.length > 0 ? (
            <div className="flex items-center">
              <ResponsiveContainer width="60%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {categoryData.map((category, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
                    <span className="text-sm text-gray-600">{category.name}</span>
                    <span className="text-sm font-medium">{category.value}%</span>
                    <span className="text-xs text-gray-400">({category.count})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[300px] flex flex-col items-center justify-center text-center px-4">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                <PieChartIcon className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-sm font-medium text-gray-600">No categories yet</p>
              <p className="text-xs text-gray-400 mt-1 max-w-[240px]">
                Category breakdown will show once emails are classified by the AI router.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AI Automation Performance */}
      {agentMetrics && agentMetrics.agents.length > 0 && (() => {
        const total = agentMetrics.total_tickets
        const assigned = agentMetrics.agents
          .filter(a => a.agent_id !== 'unassigned' && a.agent_name !== 'SPAM' && a.agent_name !== 'Needs Review')
          .reduce((sum, a) => sum + a.tickets_total, 0)
        const unassigned = agentMetrics.agents.find(a => a.agent_id === 'unassigned')?.tickets_total || 0
        const spam = agentMetrics.agents.find(a => a.agent_name === 'SPAM')?.tickets_total || 0
        const needsReview = agentMetrics.agents.find(a => a.agent_name === 'Needs Review')?.tickets_total || 0
        const classificationRate = total > 0 ? Math.round((assigned / total) * 100) : 0
        const sortedAgents = agentMetrics.agents
          .filter(a => a.agent_id !== 'unassigned' && a.agent_name !== 'SPAM' && a.agent_name !== 'Needs Review')
          .sort((a, b) => b.tickets_total - a.tickets_total)

        return (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-6">
              <Zap className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-semibold">AI Automation Performance</h3>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{classificationRate}%</p>
                <p className="text-xs text-green-600 mt-1">Auto-Classified</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{assigned}</p>
                <p className="text-xs text-blue-600 mt-1">Agent Assigned</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">{unassigned + needsReview}</p>
                <p className="text-xs text-yellow-600 mt-1">Unassigned / Review</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{spam}</p>
                <p className="text-xs text-red-600 mt-1">Spam Detected</p>
              </div>
            </div>

            {/* Agent Assignment Table */}
            {sortedAgents.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Bot className="w-4 h-4" />
                  Agent Assignment Distribution
                </h4>
                <div className="space-y-2">
                  {sortedAgents.map((agent) => {
                    const pct = total > 0 ? Math.round((agent.tickets_total / total) * 100) : 0
                    return (
                      <div key={agent.agent_id} className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 w-40 truncate" title={agent.agent_name}>
                          {agent.agent_name}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-amber-400 h-3 rounded-full transition-all"
                            style={{ width: `${Math.max(pct, 2)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-600 w-20 text-right">
                          {agent.tickets_total} ({pct}%)
                        </span>
                        <span className="text-xs text-green-600 w-16 text-right">
                          {agent.tickets_resolved} resolved
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {/* Hourly Distribution */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold mb-4">Activity by Hour</h3>
        {responseTimeData.some(d => d.conversations > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={responseTimeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="conversations" stroke="#FCD34D" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex flex-col items-center justify-center text-center px-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
              <Activity className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-sm font-medium text-gray-600">No hourly data yet</p>
            <p className="text-xs text-gray-400 mt-1 max-w-[280px]">
              Hourly activity patterns will populate as more emails are processed over time.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
